import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import SortableButtons from './SortableButtons';
import GenericDialog from '../../GenericDialog';

import { QUESTION_TYPES, REQUIRE_OPTIONS, REQUIRE_PLACEHOLDER } from './types';
import {
  ColorFirstRow,
  ColorSecondRow,
  ColorDetailsRow,
} from './styles';
import { useTrialHasStarted } from './useTrialHasStarted';

const QuestionEditor = ({
  isOpen,
  onCancel,
  onSave,
  onDelete,
  question,
  questionTypeParent,
  intl,
}) => {
  const [questionId] = useState(question.id);
  const [questionText, setQuestionText] = useState(question.question.de || question.question.text);
  const [questionType, setQuestionType] = useState(question.type);
  const [options, setOptions] = useState(question.options || []);
  const [selectedOption, setSelectedOption] = useState();
  const [selectedOptionText, setSelectedOptionText] = useState();
  const [excludeFromMarking, setExcludeFromMarking] = useState(
    question.excludeFromMarking || false,
  );
  const [required, setRequired] = useState(question.required || false);
  const [placeholderText, setPlaceholderText] = useState(question.placeholder);
  const [latestCheckedOption, setLatestCheckedOption] = useState();
  const [detailQuestionText, setDetailQuestionText] = useState('');
  const [disableRequiredField, setDisableRequiredField] = useState(
    questionTypeParent === 'main' && !question.excludeFromMarking,
  );
  const [disableNAOptionToggle, setDisableNAOptionToggle] = useState(
    options ? options.find((o) => o.isNotApplicableOption) : false,
  );
  const trialHasStarted = useTrialHasStarted();

  const requireOptions = () => REQUIRE_OPTIONS.indexOf(questionType) > -1;
  const requirePlaceholder = () => REQUIRE_PLACEHOLDER.indexOf(questionType) > -1;

  const rowColors = [ColorFirstRow, ColorSecondRow];

  const onQuestionChange = (e) => setQuestionText(e.target.value);
  const onTypeChange = (e) => setQuestionType(e.target.value);
  const onSelectedOptionTextChange = (e) => setSelectedOptionText(e.target.value);
  const handleOnDetailQuestionChange = (e) => setDetailQuestionText(e.target.value);
  const onPlaceholderTextChange = (e) => setPlaceholderText(e.target.value);

  const addOption = (e) => {
    let newOption = {
      uuid: uuidv4(),
      de: 'Zum Verändern anklicken',
      en: 'Click to edit this option text',
    };
    if (questionTypeParent === 'context') {
      newOption = { answer: { ...newOption } };
    }

    setOptions([...options, newOption]);
    e.stopPropagation();
  };

  const deleteOption = (selectedUuid) => {
    const filterTheRest = (option) => {
      const uuid = option.uuid || (option.answer ? option.answer.uuid : undefined);
      if (!uuid) {
        return false;
      }

      return uuid !== selectedUuid;
    };

    const updatedOptions = options.filter(filterTheRest);
    setOptions([...updatedOptions]);
  };

  const saveAllLatestOptionChanges = () => {
    if (!selectedOptionText && !selectedOption && !latestCheckedOption) {
      return;
    }

    const updatedOptions = options.map((o) => {
      const uuid = o.uuid || (o.answer ? o.answer.uuid : undefined);
      if (uuid !== (selectedOption || latestCheckedOption)) {
        return o;
      }
      const newOption = { ...o };
      if (questionTypeParent !== 'context') {
        newOption.de = selectedOptionText;
        newOption.en = selectedOptionText;
        return newOption;
      }

      const updatedOptionText = selectedOptionText || newOption.answer.de;
      newOption.answer.de = updatedOptionText;
      newOption.answer.en = updatedOptionText;

      if (detailQuestionText) {
        newOption.detailQuestion.de = detailQuestionText;
        newOption.detailQuestion.en = detailQuestionText;
      }

      return newOption;
    });

    setSelectedOption(undefined);
    setSelectedOptionText(undefined);
    setDetailQuestionText(undefined);
    setLatestCheckedOption(undefined);
    setOptions(updatedOptions);
  };

  const orderQuestions = (fromIndex, toIndex) => {
    const _options = [...options];
    const newOptions = _options.move(fromIndex, toIndex);
    setOptions(newOptions);
  };

  const handleChecked = (e) => {
    saveAllLatestOptionChanges();
    const updatedOptions = options.map((o) => {
      const uuid = o.uuid || (o.answer ? o.answer.uuid : undefined);
      if (uuid !== e.target.value) {
        return o;
      }

      const newOption = { ...o };
      if (!e.target.checked) {
        setLatestCheckedOption(undefined);
        return { ...newOption, detailQuestion: undefined };
      }

      newOption.detailQuestion = {
        uuid: uuidv4(),
        de: 'Bitte spezifizieren Sie Ihre Angabe',
      };
      setLatestCheckedOption(e.target.value);
      setDetailQuestionText(newOption.detailQuestion.de);
      return newOption;
    });

    setOptions(updatedOptions);
    e.stopPropagation();
  };

  const handleNAOption = (e) => {
    const updatedOptions = options.map((o) => {
      if (o.uuid !== e.target.value) {
        return o;
      }
      const newOption = { ...o, isNotApplicableOption: e.target.checked };
      if (!e.target.checked) {
        delete newOption.isNotApplicableOption;
      }
      return newOption;
    });

    setDisableNAOptionToggle(e.target.checked);
    setOptions(updatedOptions);
    e.stopPropagation();
  };

  const renderActionButtons = () => [
    <Button
      key="save"
      variant="contained"
      onClick={(e) => {
        saveAllLatestOptionChanges();
        let _question = { ...question };
        _question.question = { ..._question.question, de: questionText, en: questionText };
        _question.type = questionType;
        _question.options = options;
        // for main questions
        _question.placeholder = placeholderText;
        // for context questions
        _question.freeTextPlaceholder = placeholderText;
        _question.excludeFromMarking = excludeFromMarking;
        _question.required = required;
        if (!requireOptions()) {
          _question = { ..._question, options: undefined };
        } else {
          _question = { ..._question, placeholder: undefined };
        }
        onSave(_question, questionId);
        e.stopPropagation();
      }}
      color="primary"
    >
      <FormattedMessage id="save" />
    </Button>,
    <Button
      key="delete"
      variant="contained"
      onClick={(e) => {
        onDelete(questionId);
        e.stopPropagation();
      }}
      color="secondary"
      disabled={trialHasStarted}
    >
      <FormattedMessage id="genericDelete" />
    </Button>,
    <Button
      variant="outlined"
      key="cancel"
      onClick={(e) => {
        onCancel();
        e.stopPropagation();
      }}
    >
      <FormattedMessage id="genericCancel" />
    </Button>,
  ];

  const renderOptionsEditor = () => (
    <Card variant="outlined">
      <CardContent>
        <List>
          {options.map((o, _index) => {
            const uuid = o.uuid || (o.answer ? o.answer.uuid : undefined);
            const text = o.de || o.answer.de;
            let uuidDetailQuestion;
            let detailText;
            if (questionTypeParent === 'context' && o.detailQuestion) {
              uuidDetailQuestion = o.detailQuestion.uuid;
              detailText = o.detailQuestion.de;
            }

            return (
              <React.Fragment key={uuid}>
                <ListItem
                  key={uuid}
                  button
                  onClick={(e) => {
                    saveAllLatestOptionChanges();
                    setSelectedOption(uuid);
                    setSelectedOptionText(text);
                    e.stopPropagation();
                  }}
                  sx={{ backgroundColor: `${rowColors[_index % 2]}` }}
                >
                  <SortableButtons
                    index={_index}
                    indexUp={_index + 1 === options.length ? _index : _index + 1}
                    indexDown={_index - 1 < 0 ? 0 : _index - 1}
                    onAnyButtonClick={orderQuestions}
                    disabled={trialHasStarted}
                  />
                  {questionTypeParent === 'context' && (
                    <ListItemIcon>
                      <Tooltip title={intl.formatMessage({ id: 'questioneditor.enabledetailquestion' })}>
                        <Checkbox
                          key={uuid}
                          edge="start"
                          checked={!!o.detailQuestion}
                          tabIndex={-1}
                          onClick={handleChecked}
                          disableRipple
                          disabled={trialHasStarted}
                          value={uuid}
                        />
                      </Tooltip>
                    </ListItemIcon>
                  )}
                  {questionTypeParent === 'main' && (
                    <ListItemIcon>
                      <Tooltip title="set NA">
                        <Checkbox
                          key={uuid}
                          edge="start"
                          checked={!!o.isNotApplicableOption}
                          tabIndex={-1}
                          onClick={handleNAOption}
                          disableRipple
                          disabled={disableNAOptionToggle && !o.isNotApplicableOption}
                          value={uuid}
                        />
                      </Tooltip>
                    </ListItemIcon>
                  )}
                  {(uuid === selectedOption)
                    ? (
                      <TextField
                        onChange={onSelectedOptionTextChange}
                        onFocus={(e) => e.target.select()}
                        value={selectedOptionText}
                        fullWidth
                        autoFocus
                        key={uuid}
                        className="w-75"
                        variant="standard"
                      />
                    )
                    : (
                      <>
                        <ListItemText primary={text} />
                        <ListItemSecondaryAction
                          onClick={(e) => {
                            e.stopPropagation();
                            if (trialHasStarted) {
                              return;
                            }
                            deleteOption(uuid);
                          }}
                        >
                          <IconButton disabled={trialHasStarted} edge="end" size="large">
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </>
                    )}
                </ListItem>
                {uuidDetailQuestion && (
                  <div className="pl4">
                    <Tooltip title={intl.formatMessage({ id: 'questioneditor.clicktoeditdetailquestion' })}>
                      <ListItem
                        button
                        onClick={(e) => {
                          saveAllLatestOptionChanges();
                          setLatestCheckedOption(uuid);
                          setDetailQuestionText(detailText);
                          e.stopPropagation();
                        }}
                        sx={{ backgroundColor: ColorDetailsRow }}
                      >
                        {latestCheckedOption === uuid ? (
                          <TextField
                            onChange={handleOnDetailQuestionChange}
                            onFocus={(e) => e.target.select()}
                            value={detailQuestionText}
                            id={uuidDetailQuestion}
                            fullWidth
                            autoFocus
                            variant="standard"
                          />
                        )
                          : (<ListItemText primary={detailText} />)}
                      </ListItem>
                    </Tooltip>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
      <CardActions>
        <Button
          onClick={addOption}
          variant="contained"
          color="primary"
          disabled={trialHasStarted}
        >
          <FormattedMessage id="questioneditor.addnewoption" />
        </Button>
      </CardActions>
    </Card>
  );

  const renderPlacholderEditor = () => (
    <Card variant="outlined">
      <CardContent>
        <TextField
          id="outlined-required"
          label={<FormattedMessage id="questioneditor.placeholdertext" />}
          value={placeholderText}
          onChange={onPlaceholderTextChange}
          onFocus={(e) => e.target.select()}
          fullWidth
          variant="standard"
        />
      </CardContent>
    </Card>
  );

  const setRequiredField = (event) => {
    setRequired(event.target.checked);
  };

  const setRequiredForGradingField = (event) => {
    setExcludeFromMarking(!event.target.checked);
    if (event.target.checked) {
      setRequired(true);
      setDisableRequiredField(true);
    } else {
      setDisableRequiredField(false);
    }
  };

  return (
    <div role="presentation" onClick={saveAllLatestOptionChanges}>
      <GenericDialog
        isOpen={isOpen}
        actions={renderActionButtons()}
      >
        <div className="pa4">
          <TextField
            id="question-text"
            label={<FormattedMessage id="questioneditor.questiontext" />}
            value={questionText}
            onChange={onQuestionChange}
            onFocus={(e) => e.target.select()}
            fullWidth
            variant="standard"
          />
          <TextField
            id="question-inner-type"
            disabled={trialHasStarted}
            select
            label={<FormattedMessage id="questiontypelist.title" />}
            value={questionType}
            SelectProps={{
              native: true,
            }}
            fullWidth
            onChange={onTypeChange}
            variant="standard"
          >
            {QUESTION_TYPES[questionTypeParent].types.map(
              (type) => <option key={type.id} value={type.id}>{type.text}</option>,
            )}
          </TextField>
          <div className="pt2">
            {requireOptions() && renderOptionsEditor()}
            {requirePlaceholder() && renderPlacholderEditor()}
          </div>
          <FormControlLabel
            control={(
              <Checkbox
                checked={required}
                onChange={setRequiredField}
                name="checkedA"
                disabled={disableRequiredField}
              />
            )}
            label={<FormattedMessage id="questioneditor.required" />}
          />
          {questionTypeParent === 'main'
            && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!excludeFromMarking}
                  onChange={setRequiredForGradingField}
                  name="checkedA"
                />
              )}
              label={<FormattedMessage id="groundtruthview.considerInGroundTruth" />}
            />
            )}
        </div>
      </GenericDialog>
    </div>
  );
};

export default injectIntl(QuestionEditor);

QuestionEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  question: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
    PropTypes.bool,
  ])).isRequired,
  questionTypeParent: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
};
